export interface Links {
  site: string;
  lab: string;
  shop: string;
  privacyurl: string;
  cookiesurl: string;
  techFeedbackLink: string;
}

export interface Localization {
  siteLanguage: {
    hreflang: string;
    languageId: string;
    locale: string;
    twoLetterIsoCode: string;
  };
  solrCore: string;
  system: string;
  content: string;
  languageSelector: {
    enable: boolean;
    defaultLangISO: string;
    defaultLangTitle: string;
    availableLanguages: object;
  };
}

export interface Metadata {
  title: string;
  subtitle: string;
  copyright: string;
  isbn13: string;
  // eslint-disable-next-line camelcase
  isbn13_ebook: number;
  // eslint-disable-next-line camelcase
  identifier_ebook: number;
  // eslint-disable-next-line camelcase
  copyright_year: string;
  // eslint-disable-next-line camelcase
  edition_number: string;
  publisher: string;
  author: string;
  editor: string;
  coverImage: string;
  links: Links;
  localization: Localization;
}

export interface Info {
  id: string;
  company: string;
  usergroups: string;
  experimentalCompaniesEnabled: string;
  userName: string;
  email: string;
  externalUserId: string;
}

export interface User {
  info: Info;
  isIpAccess: boolean;
  loggedInNoAccess: boolean;
  signInAsUser: boolean;
}

export interface Sso {
  // eslint-disable-next-line camelcase
  url_account_home: string;
  // eslint-disable-next-line camelcase
  url_activate_ekey: string;
  // eslint-disable-next-line camelcase
  url_edit_profile: string;
  // eslint-disable-next-line camelcase
  url_new_account: string;
  // eslint-disable-next-line camelcase
  url_signin: string;
  // eslint-disable-next-line camelcase
  url_signout: string;
  // eslint-disable-next-line camelcase
  sso_domain: string;
  // eslint-disable-next-line camelcase
  sso_cookie_domain: string;
  myaccountId: string;
  'myaccount-authtoken': string;
  // eslint-disable-next-line camelcase
  cross_domain: {
    enabled: boolean;
    loginUrl: string;
    isLogggedInUrl: string;
  };
  // eslint-disable-next-line camelcase
  gas_service: {
    enabled: boolean;
    callbackUrlSuccess: string;
    // eslint-disable-next-line camelcase
    is_logged_in: string;
    clientGASWebSite: string;
  };
}

export interface Highlight {
  endpoint: string;
  hmac: string;
}

export interface UserSettingsService {
  endpoint: string;
  hmac: string;
}

export interface WritingTask {
  endpoint: string;
  hmac: string;
}

export interface Cookieconsentlog {
  endpoint: string;
}

export interface Polly {
  endpoint: string;
  hash: string;
  timestamp: string;
  local: boolean;
}

export interface Search {
  endpoint: string;
  hash: string;
}

export interface Endpoints {
  highlight: Highlight;
  usersettingsservice: UserSettingsService;
  cookieconsentlog: Cookieconsentlog;
  polly: Polly;
  search: Search;
  writingtask: WritingTask;
}

export interface Environment {
  customer: string;
  featureflags: any[];
  stackpurpose: string;
  installationpurpose: string;
  type: string;
  context: string;
  endpoints: Endpoints;
}

export interface Custom {
  tool1: number;
  tool2: number;
}

export interface Tools {
  calculator: number;
  cas: number;
  graph: number;
  periodictable: number;
  custom: Custom;
}

export interface Settings {
  useCourseBuilder: number;
  searchPageId: number;
  tools: Tools;
  colophonPid: number;
  teachersAreaPid: number;
  showCopyright: boolean;
  featuregallery: string;
  aboutPagePid: number;
  ebookPagePid: number;
  sourcePagePid: number;
  noFrontendView: boolean;
  myAccountSearchPageId: number;
  showBuyAccess: boolean;
  tocPagePid: number;
  showMaterialListDisclaimer: boolean;
}

export interface Colors {
  base1: string;
  base2: string;
  background: string;
  contrast: string;
  text: string;
  light: boolean;
  title: string;
}

export interface Title {
  name: string;
  url: string;
  defaultSize: string;
}

export interface System {
  name: string;
  url: string;
  defaultSize: string;
}

export interface Content {
  name: string;
  url: string;
  defaultSize: string;
}

export interface Fonts {
  title: Title;
  system: System;
  content: Content;
}

export interface Logo {
  favicon: string;
  brandlogo: string;
  logo: string;
  url: string;
}

export interface Appearance {
  colors: Colors;
  fonts: Fonts;
  logo: Logo;
}

export interface Site {
  metadata: Metadata;
  user: User;
  sso: Sso;
  environment: Environment;
  settings: Settings;
  appearance: Appearance;
}

export interface Copyright {
  vDEF: string;
}

export enum DokType {
  DOKTYPE_DEFAULT = 1
}

export interface Link {
  uid: number;
  pid: number;
  title: string;
  // eslint-disable-next-line camelcase
  fe_group: string;
  keywords: string | null;
  description: string | null;
  author: string;
  // eslint-disable-next-line camelcase
  nav_title: string;
  // eslint-disable-next-line camelcase
  nav_hide: number;
  children: Link[];
  linktext: string;
  link: string;
  accessGranted: boolean;
  doktype: DokType;
  // eslint-disable-next-line camelcase
  pbook_page: number;
}

export interface Navigation {
  author: string;
  // eslint-disable-next-line camelcase
  author_email: string;
  copyright: string;
  description: string;
  extendToSubpages: number;
  // eslint-disable-next-line camelcase
  fe_group: string;
  keywords: string;
  // eslint-disable-next-line camelcase
  nav_hide: number;
  // eslint-disable-next-line camelcase
  nav_title: string;
  pid: number;
  siteauthors: string;
  sitesubtitle: string;
  sitetitle: string;
  slug: string;
  subtitle: string;
  // eslint-disable-next-line camelcase
  sys_language_uid: number;
  title: string;
  // eslint-disable-next-line camelcase
  tx_extendedcms_classnames: string;
  uid: number;
  children: Link[];
}

export interface Teachermenu extends Link {}

export interface PageContentInfo {
  chapter?: number;
  images?: number;
  pbookPage?: number;
  standardPage?: number;
  words?: number;
  videos?: number;
  interactiveElements?: number;
  originalMaterial?: number;
  characters?: number;
  soundFiles?: number;
  iPages?: number;
  math?: number;
  quizzes?: number;
  ogStandardPage?: number;
  chapterInfo?: PageContentInfo;
}

export interface PageTreeData {
  uid: PageContentInfo;
}
